import { useState } from "react";
import { Row, Col, Table, Badge } from "react-bootstrap";
import CalcShell from "../../components/CalcShell";
import CalcField from "../../components/CalcField";
import { formatINR } from "../../utils/format";

const meta = { slug: "income-tax", emoji: "📋", title: "Income Tax Calculator", desc: "Compare old vs new regime for FY 2025-26." };

// Apply progressive slabs to a taxable income.
function slabTax(income, slabs) {
  let tax = 0;
  for (const [from, to, rate] of slabs) {
    if (income > from) {
      tax += (Math.min(income, to) - from) * rate;
    }
  }
  return tax;
}

// New regime FY 2025-26 (AY 2026-27)
const NEW_SLABS = [
  [0, 400000, 0], [400000, 800000, 0.05], [800000, 1200000, 0.1],
  [1200000, 1600000, 0.15], [1600000, 2000000, 0.2],
  [2000000, 2400000, 0.25], [2400000, Infinity, 0.3],
];
// Old regime
const OLD_SLABS = [
  [0, 250000, 0], [250000, 500000, 0.05],
  [500000, 1000000, 0.2], [1000000, Infinity, 0.3],
];

function compute(gross, deductions, regime) {
  let taxable, tax;
  if (regime === "new") {
    taxable = Math.max(gross - 75000, 0); // standard deduction only
    tax = slabTax(taxable, NEW_SLABS);
    if (taxable <= 1200000) tax = 0; // 87A rebate
  } else {
    taxable = Math.max(gross - 50000 - deductions, 0);
    tax = slabTax(taxable, OLD_SLABS);
    if (taxable <= 500000) tax = 0; // 87A rebate
  }
  const cess = tax * 0.04;
  return { taxable, tax, cess, total: tax + cess };
}

function IncomeTaxCalculator() {
  const [gross, setGross] = useState(1200000);
  const [deductions, setDeductions] = useState(150000);

  const oldR = compute(gross, deductions, "old");
  const newR = compute(gross, deductions, "new");
  const better = newR.total <= oldR.total ? "new" : "old";
  const savings = Math.abs(oldR.total - newR.total);

  return (
    <CalcShell
      meta={meta}
      note={
        <span>
          <strong>Note:</strong> Based on FY 2025-26 slabs incl. ₹75k standard
          deduction (new) / ₹50k (old) and 4% cess. The new regime gives a full
          rebate up to ₹12L taxable income. Surcharge for very high incomes is not
          included. This is an estimate consult a tax professional for filing.
        </span>
      }
    >
      <Row>
        <Col md={5}>
          <CalcField label="Gross annual income (₹)" value={gross} onChange={setGross} min={300000} max={10000000} step={50000} prefix="₹" />
          <CalcField label="Deductions (80C, 80D, HRA…) old regime" value={deductions} onChange={setDeductions} min={0} max={500000} step={10000} prefix="₹" />
        </Col>
        <Col md={7}>
          <div className="fi-calc-result mb-3 text-center">
            <div>
              The <strong className="text-warning">{better === "new" ? "New" : "Old"} regime</strong> is better for you
            </div>
            <div className="big">Save {formatINR(savings)}</div>
          </div>
          <Table bordered className="mb-0 bg-white">
            <thead>
              <tr>
                <th></th>
                <th className="text-center">
                  Old Regime{better === "old" && <Badge bg="success" className="ms-1">Best</Badge>}
                </th>
                <th className="text-center">
                  New Regime{better === "new" && <Badge bg="success" className="ms-1">Best</Badge>}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Taxable income</td>
                <td className="text-center">{formatINR(oldR.taxable)}</td>
                <td className="text-center">{formatINR(newR.taxable)}</td>
              </tr>
              <tr>
                <td>Income tax</td>
                <td className="text-center">{formatINR(oldR.tax)}</td>
                <td className="text-center">{formatINR(newR.tax)}</td>
              </tr>
              <tr>
                <td>Health & edu cess (4%)</td>
                <td className="text-center">{formatINR(oldR.cess)}</td>
                <td className="text-center">{formatINR(newR.cess)}</td>
              </tr>
              <tr className="fw-bold">
                <td>Total tax payable</td>
                <td className="text-center">{formatINR(oldR.total)}</td>
                <td className="text-center">{formatINR(newR.total)}</td>
              </tr>
            </tbody>
          </Table>
        </Col>
      </Row>
    </CalcShell>
  );
}

export default IncomeTaxCalculator;
